import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EntityType, PropertyDefinition } from '../types';
import { resolveChartWhereForApi } from '../chart/resolveChartWhereForApi';
import { ChartWhereFilterPropertyOption } from '../components/ChartWhereFilterBuilder';

/**
 * Shared `where`/`includeArchived` filter state for entries views (RepositoryEntriesView, TimelineView):
 * local state hydrated from and synced back to the `where`/`includeArchived` URL search params, plus the
 * derived bits `RepositoryEntriesFilterArea`/`EntriesFilterDialog` need. Table-column visibility (desktop
 * `EntriesTable` only) isn't part of this — it stays local to whichever view supports it.
 */
export function useEntriesFilterState(propertyDefinitions: PropertyDefinition[]) {
	const [searchParams, setSearchParams] = useSearchParams();
	const [whereFilter, setWhereFilter] = useState('');
	const [includeArchived, setIncludeArchived] = useState(false);
	const [isFilterStateHydratedFromUrl, setIsFilterStateHydratedFromUrl] = useState(false);
	const [isEntriesFilterDialogOpen, setIsEntriesFilterDialogOpen] = useState(false);
	const resolvedDataFilter = useMemo(() => resolveChartWhereForApi(whereFilter), [whereFilter]);

	useEffect(() => {
		const whereFromUrl = searchParams.get('where') ?? '';
		const includeArchivedFromUrl = searchParams.get('includeArchived');
		setWhereFilter(whereFromUrl);
		if (includeArchivedFromUrl === 'true' || includeArchivedFromUrl === 'false') {
			setIncludeArchived(includeArchivedFromUrl === 'true');
		}
		setIsFilterStateHydratedFromUrl(true);
	}, [searchParams]);

	useEffect(() => {
		if (!isFilterStateHydratedFromUrl) {
			return;
		}
		const nextParams = new URLSearchParams(searchParams);
		if (whereFilter.trim()) {
			nextParams.set('where', whereFilter);
		} else {
			nextParams.delete('where');
		}
		nextParams.set('includeArchived', includeArchived ? 'true' : 'false');
		if (nextParams.toString() === searchParams.toString()) {
			return;
		}
		setSearchParams(nextParams, { replace: true });
	}, [includeArchived, isFilterStateHydratedFromUrl, searchParams, setSearchParams, whereFilter]);

	const chartFilterPropertyOptions = useMemo(
		(): ChartWhereFilterPropertyOption[] =>
			propertyDefinitions.map((property) => ({
				slug: property.slug,
				label: property.label?.trim() ? property.label : property.slug,
				type: property.type,
				required: (property as { required?: boolean }).required === true,
				targetRepositoryId:
					property.type === EntityType.PropertyDefinitionBelongsTo
						? String((property as { targetRepositoryId?: string }).targetRepositoryId ?? '').trim()
						: '',
			})),
		[propertyDefinitions],
	);

	const handleRemoveWhereFilterChip = useCallback((slug: string) => {
		const raw = whereFilter.trim();
		if (!raw) {
			return;
		}
		try {
			const doc = JSON.parse(raw) as Record<string, unknown>;
			delete doc[slug];
			setWhereFilter(Object.keys(doc).length > 0 ? JSON.stringify(doc) : '');
		} catch {
			setWhereFilter('');
		}
	}, [whereFilter]);

	const openFilters = useCallback(() => setIsEntriesFilterDialogOpen(true), []);
	const closeFilters = useCallback(() => setIsEntriesFilterDialogOpen(false), []);

	const applyWhereAndArchived = useCallback((payload: { where: string; includeArchived: boolean }) => {
		setWhereFilter(payload.where);
		setIncludeArchived(payload.includeArchived);
	}, []);

	return {
		whereFilter,
		includeArchived,
		setIncludeArchived,
		resolvedDataFilter,
		isEntriesFilterDialogOpen,
		openFilters,
		closeFilters,
		chartFilterPropertyOptions,
		handleRemoveWhereFilterChip,
		applyWhereAndArchived,
	};
}
